import { InfoOutlined } from '@mui/icons-material';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import React, { ReactNode, useContext } from 'react';

import { Answer, Question, QuestionType } from '@/domain/impact/impact.entity';
import { language, translate } from '@/ui/i18n';
import { ImpactQuestionContext } from '@/ui/pages/app/components/quiz-panel/ImpactQuestionContext';
import { ColorsRaw } from '@/ui/theme/colors-raw';

export interface IProps {
  parameters: Question;
  children?: React.ReactNode;
  debugMode: boolean;
}

export function InputContainer({ parameters, children, debugMode }: IProps) {
  const { panelOpen, hideCompleted } = useContext(ImpactQuestionContext);

  return (
    <>
      {!(hideCompleted && parameters.answer?.value) && (
        <Box
          sx={{
            border: panelOpen
              ? `1px solid ${ColorsRaw.anthracite.Anthracite_5}`
              : `1px solid ${ColorsRaw.anthracite.Anthracite_3}`,
            borderRadius: '12px',
            p: 2,
            mt: 2,
            width: '100%',
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <Typography
              sx={{
                color: panelOpen ? '' : ColorsRaw.main.Navy_Light,
                fontWeight: panelOpen ? 'bold' : '',
                fontSize: panelOpen ? 20 : 16,
                mb: 1,
              }}
            >
              {parameters.title[language]}
            </Typography>
            {parameters.helpText && parameters.helpText[language] && (
              <div>
                <Tooltip title={parameters.helpText[language]}>
                  <Box sx={{ color: ColorsRaw.anthracite.Anthracite_5 }}>
                    <InfoOutlined color="inherit" />
                  </Box>
                </Tooltip>
              </div>
            )}
          </Stack>

          {panelOpen ? (
            children
          ) : (
            <InputValue response={parameters.answer} parameters={parameters} />
          )}
          {!!parameters.answer && !!debugMode && (
            <Typography
              sx={{
                color: ColorsRaw.anthracite.Anthracite_5,
                fontSize: 16,
                mt: 1,
              }}
            >
              {`Score: ${parameters.answer.score}`}
            </Typography>
          )}
        </Box>
      )}
    </>
  );
}

const InputValue = ({
  response,
  parameters,
}: {
  response?: Answer;
  parameters: Question;
}): ReactNode => {
  const getFormattedValue = () => {
    switch (parameters.type) {
      case QuestionType.PERCENT_SLIDER:
        return <TypoValue>{`${response?.value}%`}</TypoValue>;
      case QuestionType.FACTOR_CAPACITY: {
        const values = response?.value.split('##');
        const result =
          !values || !values[0] || +values[1] <= 0
            ? 0
            : new Intl.NumberFormat(language, {
                maximumSignificantDigits: 3,
              }).format(+values[0] / +values[1]);
        return (
          <Box>
            <TypoValue>{`${result} ${translate('Form.per-person')}`}</TypoValue>
          </Box>
        );
      }
      case QuestionType.FACTOR:
      case QuestionType.FACTOR_PERCENTAGE:
      case QuestionType.FACTOR_MINISTRY:
      case QuestionType.FRACTION:
      case QuestionType.FACTOR_MINORITY:
        return (
          <Box>
            {response?.value.split('##').map((value, index) => (
              <div key={response?.paramId + index}>
                <TypoValue>
                  {parameters.fields &&
                    parameters.fields[index]?.title[language]}
                  : {value}
                </TypoValue>
              </div>
            ))}
          </Box>
        );
      case QuestionType.SELECT:
        return (
          <TypoValue>
            {parameters.options &&
              parameters.options.filter(
                (option) => option.optionId === response?.value,
              )[0]?.title[language]}
          </TypoValue>
        );
      case QuestionType.BOOLEAN:
        return (
          <TypoValue>
            {translate(`Form.${response?.value === 'true' ? 'yes' : 'no'}`)}
          </TypoValue>
        );
      default:
        return <TypoValue>{response?.value}</TypoValue>;
    }
  };

  return response ? (
    getFormattedValue()
  ) : (
    <TypoValue>{translate('Form.no-response')}</TypoValue>
  );
};

const TypoValue = ({ children }: { children: ReactNode }) => (
  <Typography sx={{ fontSize: 20, fontWeight: 'bold' }}>{children}</Typography>
);
